import { AxiosInstance } from 'axios';
import {
    ErrorInterpretationDto,
    ErrorOwner,
    ErrorRelationState,
} from './api';
import { ReportObjectStatus } from './report';

/**
 * The backlog of failed objects across every customer. Mirrors the DTOs of IssuesController
 * on the server.
 */

export interface Issue {
    customer: string;
    environment: string;
    key: string;
    objectType: string;
    eventType?: string | null;
    relationId: string;
    objectGuid?: string | null;
    chargebeeCustomerId?: string | null;
    invoiceId?: string | null;
    code: string;
    title: string;
    owner?: ErrorOwner | null;
    status: ReportObjectStatus;
    exactState: ErrorRelationState;
    resolution?: string | null;
    requeueHelps: boolean;
    unrecognised: boolean;
    attempts: number;
    firstFailedAt?: string | null;
    lastFailedAt?: string | null;
    resolvedAt?: string | null;
    lastCheckedAt?: string | null;
    customerNotifiedAt?: string | null;
    errorType?: string | null;
    errorMessage?: string | null;
    location?: string | null;
    interpretation?: ErrorInterpretationDto | null;
    autoRequeueState?: AutoRequeueState | null;
    autoRequeueCount: number;
    autoRequeueNote?: string | null;
    autoRequeueBy?: string | null;
    nextAutoRequeueAt?: string | null;
    /** What the scanner checked itself, such as whether the invoice of a payment is in Exact. */
    checks: IssueCheck[];
    /** Whether the scanner checks this kind of problem itself, so it needs no manual steps. */
    checkable?: boolean;
    /** When the scanner last checked it; absent when no check applies. */
    checksAt?: string | null;
    /** Whether Exact itself was asked in that check. */
    checkedInExact?: boolean;
    /** What the scanner proposes besides offering the message again: Replay, Relink, or Link to a booking Exact has already. */
    autoAction?: 'Replay' | 'Relink' | 'Link' | null;
    /** That action in words. */
    autoActionText?: string | null;
    closedAt?: string | null;
    closedBy?: string | null;
    closedReason?: string | null;
    /** Why it was closed, a CloseKind. */
    closedKind?: CloseKind | null;
    /** What a re-evaluation last changed on it (reopened, reclassified) and why, with the run id. */
    reviewNote?: string | null;
    reviewedBy?: string | null;
    reviewedAt?: string | null;
    /** Whether a person may choose the Exact account of the customer: the screen "Relatie kiezen". */
    canChooseAccount?: boolean;
}

export interface IssueCheck {
    text: string;
    ok: boolean;
}

/** What became of the issues of a kind of problem that were dealt with. Mirrors IssueOutcomesDto. */
export interface IssueOutcomes {
    resolved: number;
    closed: number;
    closedKind?: CloseKind | null;
    requeued: number;
    requeueResolved: number;
    notified: number;
    notifiedResolved: number;
}

/** Someone looking into a kind of problem. Mirrors IssueInvestigationDto. */
export interface IssueInvestigation {
    code: string;
    title?: string | null;
    environment?: string | null;
    startedBy?: string | null;
    startedAt: string;
    note?: string | null;
}

export interface IssueGroup {
    code: string;
    title: string;
    /** Who the rule says has to act; who has to act now follows from the counts per status. */
    owner?: ErrorOwner | null;
    unrecognised: boolean;
    /** The open issues that match the filters. */
    open: number;
    /** Resolved or closed. */
    resolved: number;
    attempts: number;
    environments: string[];
    /** When the oldest open one first failed. */
    firstFailedAt?: string | null;
    /** When an open one last failed. */
    lastFailedAt?: string | null;
    waitingOnUs: number;
    waitingOnCustomer: number;
    needsRequeue: number;
    resolvesItself: number;
    /** Open ones that first failed in the last week. */
    new: number;
    outcomes: IssueOutcomes;
    investigation?: IssueInvestigation | null;
}

/**
 * The numbers on the status and age buttons. Per status within the age chosen, per age within
 * the status chosen: each button shows what clicking it gives.
 */
export interface IssueCounts {
    open: number;
    waitingOnUs: number;
    waitingOnCustomer: number;
    needsRequeue: number;
    resolvesItself: number;
    resolved?: number | null;
    recent: number;
    backlog: number;
}

export interface IssueList {
    since: string;
    total: number;
    open: number;
    resolved: number;
    truncated: boolean;
    /** Open issues left out because only the recent ones were asked for. */
    olderOpen: number;
    counts: IssueCounts;
    /** Across every environment the list waits for a kind of problem or a status. */
    itemsOmitted: boolean;
    groups: IssueGroup[];
    items: Issue[];
}

/** Why a person closed an issue. Mirrors IssueCloseKind on the server. */
export type CloseKind = 'customer-fixed' | 'fixed-by-us' | 'duplicate' | 'not-relevant' | 'connector-bug' | 'other';

export const CLOSE_KINDS: Record<CloseKind, string> = {
    'customer-fixed': 'Klant heeft het opgelost',
    'fixed-by-us': 'Wij hebben het opgelost',
    duplicate: 'Dubbel of al geboekt',
    'not-relevant': 'Doet er niet meer toe',
    'connector-bug': 'Fout in de connector',
    other: 'Andere reden',
};

/** Recent leaves out the open issues older than the age limit, backlog keeps only those. */
export type IssueAge = '' | 'recent' | 'backlog';

export interface IssueEnvironment {
    customer: string;
    environment: string;
    language: string;
    /** The language the mail is in: Dutch, or English for every other language. */
    mailLanguage: string;
    /** "prod" or "test", from the name of the environment. */
    stage: Stage;
    proposalsWaiting: number;
    /** Of those, the ones a person approved, waiting for their portion. */
    proposalsApproved: number;
    /** Open issues that first failed longer ago than the age limit. */
    openBacklog: number;
    /** Why the last round sent no requeues, when it held back. */
    requeueHeld?: string | null;
    lastFound: number;
    lastResolved: number;
    swept: boolean;
    backfillDone: boolean;
    backfillFrom?: string | null;
    watermark?: string | null;
    lastRunAt?: string | null;
    lastSuccessAt?: string | null;
    lastError?: string | null;
    lastDurationMs: number;
    total: number;
    open: number;
    waitingOnCustomer: number;
    waitingOnUs: number;
    resolvesItself: number;
    needsRequeue: number;
    resolved: number;
    customerNotTold: number;
    oldestOpenAt?: string | null;
    /** When an open issue last failed: whether the problems still happen. */
    latestOpenFailureAt?: string | null;
    /** Messages waiting on the Exact send queue at the last sweep; null when unknown. */
    exactWaiting?: number | null;
    /** Of those, the ones that failed five times and sit in poison. */
    exactPoison?: number | null;
    /** Daily Exact calls left at the connector's last reading. */
    exactRemaining?: number | null;
    exactLimit?: number | null;
    exactResetAt?: string | null;
    /** When the daily comparison with Chargebee last finished a pass. */
    neverArrivedAt?: string | null;
    /** Open issues of objects Chargebee has and the connector never received. */
    neverArrivedOpen?: number;
    /** How far that comparison stands when it is not up to date. */
    neverArrivedNote?: string | null;
    /** What the last scan did with the poison queues; null when it did not read them. */
    poisonReport?: PoisonReport | null;
}

/** One pass of the scanner over the poison queues of an environment. */
export interface PoisonReport {
    /** Whether it deleted, or only said what it would delete. */
    deleting: boolean;
    at: string;
    read: number;
    canGo: number;
    deleted: number;
    /** A queue held more than one scan reads. */
    more: boolean;
    error?: string | null;
    groups: { queue: string; verdict: string; label: string; deletes: boolean; count: number }[];
    sample: { queue: string; messageId: string; body: string; verdict: string; reason: string; deleted: boolean }[];
}

export interface IssueNotice {
    customer: string;
    environment: string;
    language: string;
    customerLanguage: string;
    emailAddress?: string | null;
    needed: boolean;
    reason: string;
    subject?: string | null;
    body?: string | null;
    keys: string[];
    new: number;
    withoutText: number;
}

export interface UnrecognisedError {
    code: string;
    pattern: string;
    errorType?: string | null;
    objectTypes: string[];
    /** The events it failed on: Create, Update, Delete. */
    eventTypes: string[];
    locations: string[];
    objects: number;
    open: number;
    attempts: number;
    environments: string[];
    firstFailedAt?: string | null;
    lastFailedAt?: string | null;
    samples: string[];
    examples: string[];
    /** The same objects with what the issue knows about each. */
    exampleDetails: UnrecognisedExample[];
    /** The connector and SDK frames of the newest attempt that has a stack trace. */
    stackTrace?: string | null;
}

export interface UnrecognisedExample {
    object: string;
    eventType?: string | null;
    exactState?: string | null;
    invoiceId?: string | null;
    chargebeeCustomerId?: string | null;
    location?: string | null;
    status?: string | null;
}

/** Production, test, or both. */
export type Stage = 'prod' | 'test';

export interface IssueQuery {
    stage?: Stage | '';
    customer?: string;
    environment?: string;
    days?: number;
    status?: string;
    owner?: ErrorOwner | '';
    code?: string;
    q?: string;
    age?: IssueAge;
}

/** The languages a customer can have. Only Dutch customers get a Dutch mail; the rest English. */
export const LANGUAGES: Record<string, string> = {
    nl: 'Nederlands',
    en: 'English',
    de: 'Deutsch (mail Engels)',
    fr: 'Français (mail Engels)',
    es: 'Español (mail Engels)',
    it: 'Italiano (mail Engels)',
};

// The generated client's axios instance hands the body back as text.
const body = <T>(data: unknown): T =>
    (typeof data === 'string' && data.length > 0 ? JSON.parse(data) : data) as T;

const segment = (value: string) => encodeURIComponent(value);

export const getIssueOverview = async (
    axios: AxiosInstance,
    signal?: AbortSignal
): Promise<IssueEnvironment[]> =>
    body<IssueEnvironment[]>((await axios.get('/Issues/overview', { signal })).data);

export const getIssues = async (
    axios: AxiosInstance,
    query: IssueQuery,
    signal?: AbortSignal
): Promise<IssueList> => {
    const params = new URLSearchParams();

    Object.entries(query).forEach(([name, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.set(name, String(value));
        }
    });

    const response = await axios.get(`/Issues?${params.toString()}`, { signal });

    return body<IssueList>(response.data);
};

export const getUnrecognised = async (
    axios: AxiosInstance,
    days?: number,
    stage?: Stage | '',
    signal?: AbortSignal
): Promise<UnrecognisedError[]> =>
    body<UnrecognisedError[]>(
        (await axios.get(`/Issues/unrecognised?days=${days ?? 365}${stage ? `&stage=${stage}` : ''}`, { signal })).data
    );

export const getIssueNotice = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    all: boolean,
    signal?: AbortSignal
): Promise<IssueNotice> =>
    body<IssueNotice>(
        (
            await axios.get(
                `/Issues/${segment(customer)}/${segment(environment)}/notice?all=${all}`,
                { signal }
            )
        ).data
    );

export interface NoticeSent {
    keys: string[];
    language: string;
    subject?: string | null;
    /** The text the dashboard proposed. */
    proposed?: string | null;
    /** The text as it went out; kept to learn the house style from. */
    sent?: string | null;
}

export const markNoticeSent = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    sent: NoticeSent
): Promise<void> => {
    await axios.post(
        `/Issues/${segment(customer)}/${segment(environment)}/notice/sent`,
        sent
    );
};

export const setIssueLanguage = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    language: string
): Promise<void> => {
    await axios.put(
        `/Issues/${segment(customer)}/${segment(environment)}/language`,
        { language }
    );
};

/** How far the scan of one environment is. */
export interface SweepProgress {
    /** What it is doing: "issues controleren (12 van 40)". */
    phase: string;
    percent: number;
    startedAt: string;
}

/** How far the scan of an environment is, or null when none runs. */
export const getSweepProgress = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<SweepProgress | null> => {
    const response = await axios.get(`/Issues/${segment(customer)}/${segment(environment)}/sweep/progress`);

    return response.status === 204 || !response.data ? null : body<SweepProgress>(response.data);
};

export const sweepNow = async (
    axios: AxiosInstance,
    customer: string,
    environment: string
): Promise<IssueEnvironment> =>
    body<IssueEnvironment>(
        (await axios.post(`/Issues/${segment(customer)}/${segment(environment)}/sweep`)).data
    );

/** Where the automatic requeue of one issue stands. */
export type AutoRequeueState =
    | 'Proposed'
    | 'Queued'
    | 'DryRun'
    | 'Rejected'
    | 'Blocked'
    | 'Failed'
    | 'Approved';

export const REQUEUE_STATE_LABEL: Record<AutoRequeueState, string> = {
    Proposed: 'Voorgesteld',
    Queued: 'Opnieuw aangeboden',
    DryRun: 'Proefdraai',
    Rejected: 'Afgewezen',
    Blocked: 'Geweigerd',
    Failed: 'Mislukt',
    Approved: 'Goedgekeurd, gaat mee in de volgende porties',
};

export interface SweepRound {
    state: 'Running' | 'Done' | 'Failed' | string;
    /** The environments the round was limited to, or null for all. */
    stage?: Stage | null;
    /** Says it runs, but has not been heard of for too long: the instance stopped halfway. */
    stale: boolean;
    trigger?: string | null;
    instance?: string | null;
    startedAt?: string | null;
    finishedAt?: string | null;
    nextRunAt?: string | null;
    updatedAt?: string | null;
    environments: number;
    done: number;
    failed: number;
    working: string[];
    found: number;
    resolved: number;
    reopened: number;
    proposed: number;
    requeued: number;
    error?: string | null;
    durationMs: number;
}

export interface SweeperStatus {
    enabled: boolean;
    intervalMinutes: number;
    autoRequeue: AutoRequeueMode;
    /** Whether the mode was chosen on the dashboard rather than configured. */
    autoRequeueChosen: boolean;
    autoRequeueChangedBy?: string | null;
    autoRequeueChangedAt?: string | null;
    /** The configured mode, which applies when nobody chooses on the dashboard. */
    autoRequeueConfigured: AutoRequeueMode;
    maxRequeuesPerRound: number;
    autoRequeueMaxAgeDays: number;
    maxAutoRequeues: number;
    /** False: every requeue is a dry run. */
    allowRequeue: boolean;
    storage: string;
    development: boolean;
    teamsAlerts: boolean;
    round?: SweepRound | null;
    recent: SweepRound[];
    failing: IssueEnvironment[];
    proposalsWaiting: number;
    /** Which environments the timer scans. */
    scope: ScanScope;
    /** Whether the timer is paused; a round started by hand still runs. */
    pause?: ScannerPause | null;
    /** The environments the timer scans on top of the scope, as environmentKey writes them. */
    optedIn?: string[];
}

/** Whether the timer scans one environment on top of the scope. */
export interface SweepOptIn {
    customer: string;
    environment: string;
    included: boolean;
    changedBy?: string | null;
    changedAt?: string | null;
}

export interface ScannerPause {
    paused: boolean;
    changedBy?: string | null;
    changedAt?: string | null;
}

export type ScanMode = 'prod' | 'test' | 'all' | 'selected';

export interface ScanScope {
    mode: ScanMode;
    /** For "selected": customer/environment in lower case, as environmentKey writes it. */
    environments: string[];
    /** The scope in words: "alleen productie". */
    description: string;
    changedBy?: string | null;
    changedAt?: string | null;
}

export interface RequeueProposal {
    issue: Issue;
    proposedAt?: string | null;
    offered: number;
}

export interface IssueRef {
    customer: string;
    environment: string;
    key: string;
}

export interface RequeueDecisionResult {
    item: IssueRef;
    state: AutoRequeueState | 'Gone';
    note?: string | null;
}

export interface NoticeCodeText {
    title?: string | null;
    ask?: string | null;
}

export interface NoticeStyle {
    greeting?: string | null;
    intro?: string | null;
    introOne?: string | null;
    outro?: string | null;
    signOff?: string | null;
    signature?: string | null;
    subject?: string | null;
    concerns?: string | null;
    codes: Record<string, NoticeCodeText>;
}

export interface NoticeStyleSet {
    language: string;
    style: NoticeStyle;
    defaults: NoticeStyle;
}

export interface NoticeExample {
    language: string;
    customer: string;
    environment: string;
    codes: string[];
    subject?: string | null;
    proposed?: string | null;
    sent?: string | null;
    sentAt: string;
}

export const getSweeperStatus = async (
    axios: AxiosInstance,
    stage?: Stage | '',
    signal?: AbortSignal
): Promise<SweeperStatus> =>
    body<SweeperStatus>((await axios.get(`/Issues/sweeper${stage ? `?stage=${stage}` : ''}`, { signal })).data);

/**
 * Starts a scan; `resume` leaves out what a stopped scan already did, `stage` limits it to
 * production or test environments.
 */
export const runSweeper = async (axios: AxiosInstance, resume = false, stage?: Stage | ''): Promise<void> => {
    const query = [resume ? 'resume=true' : '', stage ? `stage=${stage}` : ''].filter(Boolean).join('&');
    await axios.post(`/Issues/sweeper/run${query ? `?${query}` : ''}`);
};

/** Sets which environments the timer scans. */
export const saveScanScope = async (axios: AxiosInstance, mode: ScanMode, environments: string[]): Promise<ScanScope> =>
    body<ScanScope>((await axios.put('/Issues/sweeper/scope', { mode, environments })).data);

export const saveScannerPause = async (axios: AxiosInstance, paused: boolean): Promise<ScannerPause> =>
    body<ScannerPause>((await axios.put('/Issues/sweeper/pause', { paused })).data);

export const getSweepOptIn = async (axios: AxiosInstance, customer: string, environment: string): Promise<SweepOptIn> =>
    body<SweepOptIn>((await axios.get(`/Issues/sweeper/opt-in/${segment(customer)}/${segment(environment)}`)).data);

export const saveSweepOptIn = async (axios: AxiosInstance, customer: string, environment: string, included: boolean): Promise<SweepOptIn> =>
    body<SweepOptIn>((await axios.put(`/Issues/sweeper/opt-in/${segment(customer)}/${segment(environment)}`, { included })).data);

export const stopSweeper = async (axios: AxiosInstance): Promise<void> => {
    await axios.post('/Issues/sweeper/stop');
};

/** What the sweep does with issues a requeue would solve. */
export type AutoRequeueMode = 'Off' | 'Propose' | 'ByKind' | 'Auto';

export const MODE_LABEL: Record<AutoRequeueMode, string> = {
    Off: 'Uit',
    Propose: 'Alleen voorstellen, wij keuren alles goed',
    ByKind: 'Per soort fout',
    Auto: 'Alles automatisch, behalve omkoppelen',
};

export const MODE_HINT: Record<AutoRequeueMode, string> = {
    Off: 'De scanner stelt niets voor en biedt niets opnieuw aan, ook niet wat al is goedgekeurd.',
    Propose: 'De scanner zet alles klaar; niets gaat op een queue zonder dat iemand het goedkeurt.',
    ByKind:
        'Exact-limiet, time-outs, serverfouten en problemen waarvan de scanner in Exact zag dat de oorzaak weg is gaan vanzelf. Betalingen en ophalen uit Chargebee en aanbieden wachten op goedkeuring.',
    Auto: 'Alles wat recent is gaat vanzelf, ook betalingen. Omkoppelen nooit.',
};

/** Whether the sweep may send a group itself: Auto, Propose or Never. */
export type RequeueLane = 'Auto' | 'Propose' | 'Never';

/** One group of standing proposals. Mirrors ProposalGroupDto. */
export interface ProposalGroup {
    customer: string;
    environment: string;
    stage: Stage;
    code: string;
    title: string;
    action?: string | null;
    lane: RequeueLane;
    backlog: boolean;
    /** Whether, in the mode that applies, the sweep sends these itself. */
    automatic: boolean;
    count: number;
    approved: number;
    oldestAt?: string | null;
    latestAt?: string | null;
    /** Whether the timer scans this environment; only a scan sends the portions. */
    inScope: boolean;
    held?: string | null;
}

export interface RequeueSummary {
    mode: AutoRequeueMode;
    allowRequeue: boolean;
    maxPerRound: number;
    maxAgeDays: number;
    maxRequeues: number;
    intervalMinutes: number;
    groups: ProposalGroup[];
}

export interface RequeueProposalPage {
    total: number;
    items: RequeueProposal[];
}

/** One group to decide on at once. */
export interface RequeueGroupRef {
    customer: string;
    environment: string;
    code: string;
    lane?: RequeueLane | null;
    backlog?: boolean | null;
}

export const groupKey = (g: RequeueGroupRef) => `${g.customer}/${g.environment}/${g.code}/${g.lane ?? ''}/${g.backlog ?? ''}`.toLowerCase();

export const getRequeueSummary = async (axios: AxiosInstance, stage?: Stage | '', signal?: AbortSignal): Promise<RequeueSummary> =>
    body<RequeueSummary>((await axios.get(`/Issues/requeue/summary${stage ? `?stage=${stage}` : ''}`, { signal })).data);

export const getProposals = async (
    axios: AxiosInstance,
    group: RequeueGroupRef,
    skip: number,
    take: number,
    signal?: AbortSignal
): Promise<RequeueProposalPage> => {
    const params = new URLSearchParams({
        customer: group.customer,
        environment: group.environment,
        code: group.code,
        skip: String(skip),
        take: String(take),
    });

    if (group.lane) {
        params.set('lane', group.lane);
    }

    if (group.backlog !== undefined && group.backlog !== null) {
        params.set('backlog', String(group.backlog));
    }

    return body<RequeueProposalPage>((await axios.get(`/Issues/requeue/proposals?${params.toString()}`, { signal })).data);
};

export const approveGroup = async (axios: AxiosInstance, group: RequeueGroupRef): Promise<number> =>
    body<number>((await axios.post('/Issues/requeue/approve-group', group)).data);

export const rejectGroup = async (axios: AxiosInstance, group: RequeueGroupRef): Promise<number> =>
    body<number>((await axios.post('/Issues/requeue/reject-group', group)).data);

export const saveAutoRequeueMode = async (axios: AxiosInstance, mode: AutoRequeueMode | null): Promise<void> => {
    await axios.put('/Issues/sweeper/auto-requeue', { mode });
};

/** Mirrors IssueCloseMatchingDto. */
export interface CloseMatching {
    customer?: string | null;
    environment?: string | null;
    code: string;
    stage?: Stage | '';
    olderThanDays?: number | null;
    proposedOnly?: boolean;
    reason?: string | null;
    kind?: CloseKind | null;
    dryRun?: boolean;
}

/** Closes every open issue that matches; with dryRun only counts them. */
export const closeMatching = async (axios: AxiosInstance, close: CloseMatching): Promise<number> =>
    body<number>((await axios.post('/Issues/close/matching', close)).data);

/** One thing to look at now. Mirrors AttentionItemDto. */
export interface AttentionItem {
    severity: 'error' | 'warn' | 'info';
    kind: string;
    title: string;
    detail?: string | null;
    customer?: string | null;
    environment?: string | null;
    code?: string | null;
    count: number;
    since?: string | null;
    latestAt?: string | null;
    tab: string;
    status?: string | null;
}

/** Mirrors AttentionDto. */
export interface Attention {
    at: string;
    mode: AutoRequeueMode;
    items: AttentionItem[];
    automatic: number;
    approved: number;
    toApprove: number;
    backlogProposals: number;
    openRecent: number;
    openBacklog: number;
    waitingOnUs: number;
    mails: number;
    mailPoints: number;
    maxAgeDays: number;
    investigations: IssueInvestigation[];
    lessons: IssueLesson[];
    /** The environments whose customer has something new to hear, the most first. */
    mailsTo: AttentionMail[];
    /** Every kind of problem that waits on us, of any age, the most first. */
    ours?: AttentionOurs[];
}

/** A kind of problem with open issues that wait on us. Mirrors AttentionOursDto. */
export interface AttentionOurs {
    /** Null for an environment scanned before the scanner counted per kind of problem: then all its issues of ours are one line. */
    code?: string | null;
    title?: string | null;
    unrecognised: boolean;
    count: number;
    /** Of those, the ones that first failed longer ago than the age limit. */
    backlog: number;
    /** Where they wait, as "customer/environment". */
    environments: string[];
    oldestAt?: string | null;
    latestAt?: string | null;
}

export interface AttentionMail {
    customer: string;
    environment: string;
    points: number;
}

/** A rule the outcomes say could be better. Mirrors IssueLessonDto. */
export interface IssueLesson {
    kind: 'requeue-works' | 'requeue-fails' | 'closed-by-hand' | 'mail-does-not-help' | 'unknown-large' | string;
    code: string;
    title?: string | null;
    finding: string;
    advice: string;
    outcomes: IssueOutcomes;
    open: number;
    environments: string[];
}

export const getAttention =async (axios: AxiosInstance, stage?: Stage | '', signal?: AbortSignal): Promise<Attention> =>
    body<Attention>((await axios.get(`/Issues/attention${stage ? `?stage=${stage}` : ''}`, { signal })).data);

export const approveProposals = async (
    axios: AxiosInstance,
    items: IssueRef[]
): Promise<RequeueDecisionResult[]> =>
    body<RequeueDecisionResult[]>(
        (await axios.post('/Issues/requeue/approve', { items })).data
    );

export const rejectProposals = async (
    axios: AxiosInstance,
    items: IssueRef[]
): Promise<void> => {
    await axios.post('/Issues/requeue/reject', { items });
};

/** Closes issues that no longer matter; they open again only when the object fails again. */
/** The Chargebee customer of a relink, read live. Mirrors RelinkChargebeeCustomerDto. */
export interface RelinkChargebeeCustomer {
    id: string;
    exists: boolean;
    name?: string | null;
    company?: string | null;
    email?: string | null;
    vatNumber?: string | null;
    city?: string | null;
    country?: string | null;
    createdAt?: string | null;
}

/** An Exact account in a relink, read live. Mirrors RelinkAccountDto. */
export interface RelinkAccount {
    /** Why it is a candidate: "Zelfde code 2847", "Zelfde e-mailadres". */
    found?: string | null;
    id?: string | null;
    exists: boolean;
    code?: string | null;
    name?: string | null;
    status?: string | null;
    blocked?: boolean | null;
    email?: string | null;
    searchCode?: string | null;
    city?: string | null;
    country?: string | null;
    vatNumber?: string | null;
    chamberOfCommerce?: string | null;
    created?: string | null;
}

/** Mirrors RelinkReviewDto. */
export interface RelinkReview {
    key: string;
    customer: string;
    environment: string;
    chargebee?: RelinkChargebeeCustomer | null;
    current?: RelinkAccount | null;
    candidates: RelinkAccount[];
    proposedId?: string | null;
    /** What the person searched for, when they did. */
    query?: string | null;
    problem?: string | null;
}

export interface RelinkResult {
    state: string;
    note?: string | null;
}

/** The customer and the accounts to choose from; `query` searches Exact by name, code, e-mail or VAT number as well. */
export const getRelinkReview = async (axios: AxiosInstance, customer: string, environment: string, key: string, query?: string): Promise<RelinkReview> =>
    body<RelinkReview>(
        (
            await axios.get(
                `/Issues/${encodeURIComponent(customer)}/${encodeURIComponent(environment)}/relink/${encodeURIComponent(key)}${query ? `?q=${encodeURIComponent(query)}` : ''}`
            )
        ).data
    );

export const confirmRelink = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    key: string,
    accountId: string,
    query?: string | null
): Promise<RelinkResult> =>
    body<RelinkResult>(
        (
            await axios.post(`/Issues/${encodeURIComponent(customer)}/${encodeURIComponent(environment)}/relink/confirm`, {
                key,
                accountId,
                query: query || null,
                verified: true,
            })
        ).data
    );

/** The Chargebee document of a link, read live. Mirrors LinkChargebeeDocumentDto. */
export interface LinkChargebeeDocument {
    id: string;
    exists: boolean;
    date?: string | null;
    total?: number | null;
    currency?: string | null;
    customerId?: string | null;
    status?: string | null;
}

/** A booking in Exact the document could be tied to. Mirrors LinkEntryDto. */
export interface LinkEntry {
    entryId?: string | null;
    entryNumber?: number | null;
    journal?: string | null;
    date?: string | null;
    /** In the division's currency. */
    amount?: number | null;
    reportingYear?: number | null;
    reportingPeriod?: number | null;
    accountCode?: string | null;
    /** Whether it is booked on the account the connector has for the Chargebee customer; null when unknown. */
    sameCustomer?: boolean | null;
}

/** Mirrors LinkReviewDto. */
export interface LinkReview {
    key: string;
    customer: string;
    environment: string;
    /** "factuur CB00016245" and the like. */
    object: string;
    chargebee?: LinkChargebeeDocument | null;
    currentDependingId?: string | null;
    entries: LinkEntry[];
    proposedId?: string | null;
    problem?: string | null;
}

/** The document and the bookings Exact has under its reference, read now. */
export const getLinkReview = async (axios: AxiosInstance, customer: string, environment: string, key: string): Promise<LinkReview> =>
    body<LinkReview>((await axios.get(`/Issues/${encodeURIComponent(customer)}/${encodeURIComponent(environment)}/link/${encodeURIComponent(key)}`)).data);

/** Ties the document to the booking; the server reads Exact and the relation again before it writes. */
export const confirmLink = async (axios: AxiosInstance, customer: string, environment: string, key: string, entryId: string): Promise<RelinkResult> =>
    body<RelinkResult>(
        (
            await axios.post(`/Issues/${encodeURIComponent(customer)}/${encodeURIComponent(environment)}/link/confirm`, {
                key,
                entryId,
                verified: true,
            })
        ).data
    );

export const closeIssues = async (axios: AxiosInstance, items: IssueRef[], reason: string, kind?: CloseKind | null): Promise<number> =>
    body<number>((await axios.post('/Issues/close', { items, reason, kind })).data);

export interface InvestigationRequest {
    code: string;
    title?: string | null;
    /** Where it is started from, as "customer/environment"; empty for every environment. */
    environment?: string | null;
    note?: string | null;
}

/** Starts looking into a kind of problem, or updates the note. */
export const saveInvestigation = async (axios: AxiosInstance, request: InvestigationRequest): Promise<IssueInvestigation> =>
    body<IssueInvestigation>((await axios.put('/Issues/investigations', request)).data);

export const stopInvestigation = async (axios: AxiosInstance, code: string): Promise<void> => {
    await axios.post('/Issues/investigations/stop', { code });
};

export const reopenIssues = async (axios: AxiosInstance, items: IssueRef[]): Promise<number> =>
    body<number>((await axios.post('/Issues/reopen', { items })).data);

export const getNoticeStyle = async (
    axios: AxiosInstance,
    language: string
): Promise<NoticeStyleSet> =>
    body<NoticeStyleSet>(
        (await axios.get(`/Issues/notice/style/${segment(language)}`)).data
    );

export const saveNoticeStyle = async (
    axios: AxiosInstance,
    language: string,
    style: NoticeStyle
): Promise<void> => {
    await axios.put(`/Issues/notice/style/${segment(language)}`, style);
};

export const getNoticeExamples = async (
    axios: AxiosInstance,
    take = 100
): Promise<NoticeExample[]> =>
    body<NoticeExample[]>(
        (await axios.get(`/Issues/notice/examples?take=${take}`)).data
    );

/** Where the backlog stands on one object; mirrors IssueStateDto. */
export interface IssueState {
    objectType: string;
    relationId: string;
    key: string;
    status: string;
    code?: string | null;
    resolution?: string | null;
    resolvedAt?: string | null;
    closedAt?: string | null;
    closedReason?: string | null;
}

/** The backlog's state of each object asked for, of one environment; objects without an issue are left out. Admin only. */
export const getIssueStates = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    objects: { objectType: string; relationId: string }[],
    signal?: AbortSignal
): Promise<IssueState[]> =>
    body<IssueState[]>(
        (
            await axios.post(`/Issues/${encodeURIComponent(customer)}/${encodeURIComponent(environment)}/states`, objects, {
                headers: { 'Content-Type': 'application/json' },
                signal,
            })
        ).data
    ) ?? [];
